import { CONCESSIONARIAS, matchConcessionaria } from '../data/concessionarias'
import { normalizarTexto } from './normalizar'

// Adicionais tarifários ANEEL por bandeira (R$/KWH)
export const BANDEIRAS = {
  verde:       { label: 'Verde',        adicional: 0 },
  amarela:     { label: 'Amarela',      adicional: 0.01885 },
  vermelha_p1: { label: 'Vermelha P1',  adicional: 0.04463 },
  vermelha_p2: { label: 'Vermelha P2',  adicional: 0.07877 },
}

// Mínimo faturável por tipo de fase (KWH) — padrão ANEEL
const MINIMO_FASE = {
  monofasico: 30,
  bifasico:   50,
  trifasico: 100,
}

const DESCONTO_AXEN = 0.10

// Descritores que identificam linhas de TE e TUSD no array charges
const KEYWORDS_TE_TUSD = [
  'ENERGIA ATIVA',
  'ENERGIA ELET',
  'TARIFA DE ENERGIA',
  'TARIFA ENERGIA',
  ' TE ',
  'TUSD',
  'USO DO SISTEMA',
  'DISTRIBUICAO',
  'DISTRIBUIÇÃO',
]

const normDesc = normalizarTexto

function parseNum(v) {
  if (v == null || v === '') return null
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  const s = String(v).replace(/[^\d,.-]/g, '').replace(',', '.')
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

/**
 * Busca a tarifa base (R$/KWh) da concessionária cadastrada (total / 1000).
 * Usa o id escolhido no formulário; sem id, identifica pelo nome/aliases do texto da conta.
 * Retorna null se não encontrar ou se a tarifa não estiver preenchida.
 */
export function buscarTarifaBaseConcessionaria({ companyName, concessionariaId }) {
  let concessionaria = null

  if (concessionariaId) {
    concessionaria = CONCESSIONARIAS.find(x => String(x.id) === String(concessionariaId)) || null
  }
  if (!concessionaria && companyName) {
    concessionaria = matchConcessionaria(companyName)
  }

  return concessionaria && concessionaria.total > 0 ? concessionaria.total / 1000 : null
}

/**
 * Extrai a tarifa base (TE+TUSD) em R$/KWH a partir do array de charges.
 * Retorna null se não for possível determinar.
 */
export function extrairTarifaBase(charges) {
  if (!Array.isArray(charges) || charges.length === 0) return null

  let soma = 0
  let encontrou = false

  for (const charge of charges) {
    const desc = normDesc(charge.description)
    const unit = normDesc(charge.unit)
    const isKwh = unit === 'KWH' || unit === 'KW H' || unit.includes('KWH')
    const isEnergy = KEYWORDS_TE_TUSD.some(k => desc.includes(k.trim()))

    if (isKwh && isEnergy) {
      const preco = parseNum(charge.unit_price_with_tax)
      if (preco != null && preco > 0) {
        soma += preco
        encontrou = true
      }
    }
  }

  return encontrou ? soma : null
}

/**
 * Calcula economia mensal/anual/5anos/10anos.
 *
 * @param {number} consumo       KWH da fatura ou médio
 * @param {number} tarifaBase    R$/KWH (TE+TUSD extraído da fatura)
 * @param {string} bandeira      chave de BANDEIRAS
 * @param {string} fase          'monofasico' | 'bifasico' | 'trifasico'
 * @returns {{ creditos, descontoPorKwh, mensal, anual, cinco_anos, dez_anos }}
 */
export function calcularEconomia(consumo, tarifaBase, bandeira, fase) {
  const minimo = MINIMO_FASE[fase] || MINIMO_FASE.monofasico
  const creditos = Math.max(0, consumo - minimo)
  const descontoPorKwh = tarifaBase * DESCONTO_AXEN
  const adicional = (BANDEIRAS[bandeira] || BANDEIRAS.verde).adicional
  const mensal = creditos * (descontoPorKwh + adicional)

  return {
    creditos,
    descontoPorKwh,
    mensal,
    anual:      mensal * 12,
    cinco_anos: mensal * 12 * 5,
    dez_anos:   mensal * 12 * 10,
  }
}

/**
 * Retorna o consumo médio considerando o mês atual + até 11 meses do histórico.
 * Composição: [consumed_kwh atual, ...11 meses mais recentes do histórico]
 * Ignora entradas com consumed_kwh vazio ou zero.
 */
export function consumoMedio(consumption) {
  if (!consumption) return null

  const atual = parseNum(consumption.consumed_kwh)

  const history = Array.isArray(consumption.history) ? consumption.history : []
  const historico = history
    .map(h => parseNum(h?.consumed_kwh))
    .filter(n => n != null && n > 0)
    .slice(0, 11)

  const nums = []
  if (atual != null && atual > 0) nums.push(atual)
  nums.push(...historico)

  if (nums.length === 0) return null
  return nums.reduce((a, b) => a + b, 0) / nums.length
}

/**
 * Gera o resumo completo da simulação para persistência.
 * Retorna um objeto com economia por bandeira (mensal/anual/5 anos/10 anos)
 * para a última fatura e para a média de 12 meses, ou null se inválido.
 */
export function gerarResumoSimulacao({ extractedJson, concessionariaId, tipoFase }) {
  if (!extractedJson) return null

  // --- tarifa base (mesma prioridade de EtapaSimulacao.vue) ---
  let tarifaBase = null
  let fonteTarifa = null

  const fromCsv = buscarTarifaBaseConcessionaria({
    companyName:     extractedJson.company_name,
    concessionariaId,
  })
  if (fromCsv != null) {
    tarifaBase  = fromCsv
    fonteTarifa = 'csv'
  } else {
    const fromCharges = extrairTarifaBase(extractedJson.charges)
    if (fromCharges != null) {
      tarifaBase  = fromCharges
      fonteTarifa = 'charges'
    } else {
      const consumoRaw = parseNum(extractedJson?.consumption?.consumed_kwh)
      const fromFallback = estimarTarifaFallback(extractedJson.total_due, consumoRaw)
      if (fromFallback != null) {
        tarifaBase  = fromFallback
        fonteTarifa = 'fallback'
      }
    }
  }

  // --- consumo ---
  const consumoUltima = parseNum(extractedJson?.consumption?.consumed_kwh)
  if (tarifaBase == null || consumoUltima == null || consumoUltima <= 0) return null

  const fase           = tipoFase || 'monofasico'
  const consumoMedioVal = consumoMedio(extractedJson.consumption)

  const buildEconomia = (consumo) => {
    const resultado = {}
    for (const [key] of Object.entries(BANDEIRAS)) {
      const e = calcularEconomia(consumo, tarifaBase, key, fase)
      resultado[key] = {
        mensal:      e.mensal,
        anual:       e.anual,
        cinco_anos:  e.cinco_anos,
        dez_anos:    e.dez_anos,
      }
    }
    return resultado
  }

  const creditos = (consumo) => {
    const minimos = { monofasico: 30, bifasico: 50, trifasico: 100 }
    return Math.max(0, consumo - (minimos[fase] || 30))
  }

  const resumo = {
    fonte_tarifa:    fonteTarifa,
    tarifa_base:     tarifaBase,
    fase,
    ultima_fatura: {
      consumo:  consumoUltima,
      creditos: creditos(consumoUltima),
      economia: buildEconomia(consumoUltima),
    },
  }

  if (consumoMedioVal != null && consumoMedioVal > 0) {
    resumo.media_12_meses = {
      consumo:  consumoMedioVal,
      creditos: creditos(consumoMedioVal),
      economia: buildEconomia(consumoMedioVal),
    }
  }

  return resumo
}

/**
 * Fallback: estima tarifa base dividindo total_due pelo consumo.
 * Menos preciso mas útil se charges não tiver TE/TUSD identificáveis.
 */
export function estimarTarifaFallback(totalDue, consumo) {
  if (!totalDue || !consumo || consumo <= 0) return null
  const t = parseNum(totalDue)
  if (t == null || t <= 0) return null
  return t / consumo
}
